import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  BarChart2,
  ClipboardList,
  Film,
  FolderCog,
  LayoutDashboard,
  Menu,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { AppRoutes } from '@/app/routes';
import { Role } from '@/common/model/types';
import { useAuthController } from '@/features/auth/controller/auth.controller';
import { usePlayerAccess } from '@/features/team/controller/usePlayerAccess';
import { cn } from '@/utils/cn';
import { track } from '@/lib/analytics';
import { TAB_BAR_HEIGHT_VAR } from './tabBarHeight';

export interface Tab {
  labelKey: string;
  path: string;
  icon: LucideIcon;
  /** Screens the tab stays lit on: a team's page belongs to « Équipe », not to no tab at all. */
  prefixes: readonly string[];
}

const HOME: Tab = {
  labelKey: 'nativeApp.tabs.home',
  path: AppRoutes.dashboard.home,
  icon: LayoutDashboard,
  prefixes: [AppRoutes.dashboard.home],
};
const STATS: Tab = {
  labelKey: 'nativeApp.tabs.stats',
  path: AppRoutes.stats.main,
  icon: BarChart2,
  prefixes: [AppRoutes.stats.main],
};

const STAFF_TABS: readonly Tab[] = [
  HOME,
  {
    labelKey: 'nativeApp.tabs.matches',
    path: AppRoutes.match.admin,
    icon: FolderCog,
    prefixes: [AppRoutes.match.admin, AppRoutes.matchReview.main, AppRoutes.match.import, AppRoutes.match.setup],
  },
  STATS,
  {
    labelKey: 'nativeApp.tabs.team',
    path: AppRoutes.team.list,
    icon: Users,
    prefixes: [AppRoutes.team.list, '/team'],
  },
];

const VIDEO: Tab = {
  labelKey: 'nativeApp.tabs.video',
  path: AppRoutes.video.main,
  icon: Film,
  prefixes: [AppRoutes.video.main],
};
const PLANS: Tab = {
  labelKey: 'nativeApp.tabs.plans',
  path: AppRoutes.gamePlans.list,
  icon: ClipboardList,
  prefixes: [AppRoutes.gamePlans.list],
};

// Checked after the role's own tabs: a coach reaches Vidéo or Plans from « Plus », a player has them as tabs.
const MORE: Tab = {
  labelKey: 'nativeApp.tabs.more',
  path: AppRoutes.nativeApp.more,
  icon: Menu,
  prefixes: [
    AppRoutes.nativeApp.more,
    AppRoutes.cartography.main,
    AppRoutes.video.main,
    AppRoutes.gamePlans.list,
    AppRoutes.membership.requests,
    AppRoutes.tutorials.main,
    AppRoutes.faq.main,
    AppRoutes.profile.main,
    AppRoutes.public.pricing,
    AppRoutes.superAdmin.dashboard,
  ],
};

function matchesPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

/** The tab to light for a screen, first match in bar order; none for a screen outside the bar (sign-in…). */
export function activeTabFor(pathname: string, tabs: readonly Tab[]): Tab | undefined {
  return tabs.find((tab) => tab.prefixes.some((prefix) => matchesPrefix(pathname, prefix)));
}

/** The role's own tabs, « Plus » excluded: the « Plus » screen lists everything else. */
export function useNativeTabs(): readonly Tab[] {
  const isPlayer = useAuthController((s) => s.user?.role === Role.PLAYER);
  const access = usePlayerAccess();
  if (!isPlayer) return STAFF_TABS;
  // Hidden while the player's teams load: the access defaults to open until then.
  const open = (area: 'stats' | 'video') => !access.isLoading && access[area];
  return [HOME, ...(open('stats') ? [STATS] : []), ...(open('video') ? [VIDEO] : []), PLANS];
}

const tabClass = 'flex min-h-14 flex-1 flex-col items-center justify-center gap-0.5 px-1 text-[0.7rem] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white';

/**
 * App phones and tablets, website phones (`tabBarShown.ts`): the main screens one tap away at the bottom, the rest
 * of the menu on the « Plus » screen. Ink background, continuous with the system bars.
 */
export function NativeTabBar() {
  const { t } = useTranslation('common');
  const { pathname } = useLocation();
  const tabs = [...useNativeTabs(), MORE];
  const active = activeTabFor(pathname, tabs);

  // What sticks to the bottom of a page (import wizard buttons) sits above the bar.
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(TAB_BAR_HEIGHT_VAR, `calc(3.5rem + var(--safe-area-inset-bottom, 0px))`);
    return () => {
      root.style.removeProperty(TAB_BAR_HEIGHT_VAR);
    };
  }, []);

  return (
    <nav
      aria-label={t('nativeApp.tabs.label')}
      className="fixed inset-x-0 bottom-0 z-30 bg-action text-white pb-[var(--safe-area-inset-bottom,0px)] lg:hidden"
    >
      <ul className="flex">
        {tabs.map((tab) => {
          const { labelKey, path, icon: Icon } = tab;
          const isActive = tab === active;
          return (
            <li key={path} className="flex flex-1">
              <Link
                to={path}
                aria-current={isActive ? 'page' : undefined}
                onClick={() => track('native_tab_opened', { tab: labelKey.split('.').pop() })}
                className={cn(tabClass, 'relative', isActive ? 'text-white' : 'text-white/60')}
              >
                {/* The colour change alone was faint on the ink bar outdoors (VOL-14 lot 8). */}
                {isActive && (
                  <span
                    aria-hidden="true"
                    data-testid="active-tab-mark"
                    className="absolute inset-x-5 top-0 h-[3px] rounded-b-full bg-white"
                  />
                )}
                <Icon size={22} strokeWidth={isActive ? 2.25 : 1.75} aria-hidden="true" />
                <span className="truncate max-w-full">{t(labelKey)}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
